import { Link } from 'react-router-dom'
import dayjs from 'dayjs'
import { useTranslation } from 'react-i18next'
import { useEditor, EditorContent } from '@tiptap/react'
import { richTextDisplayExtensions } from '@/components/ui/minimal-tiptap/display-extensions'
import ProtectedContent from 'app/frontend/base/components/ProtectedContent'
import '@/components/ui/minimal-tiptap/styles/index.css'
import { Pencil } from 'lucide-react'
import { cn } from '@/lib/utils'
import { buttonVariants } from '@/components/ui/button'
import Card from 'app/frontend/base/components/Card'
import NewsTagBadge from 'app/frontend/base/pages/News/NewsTagBadge'
import { NewsPermission } from './types'
import SeoHead from '../../../components/SeoHead'
import { htmlExcerpt, siteTitle } from '../../../helpers/seo'
import { useCustomDomain } from '../../../hooks/useCustomDomain'

const NewsItemContent = ({ content }: { content: string }) => {
  const editor = useEditor({
    extensions: richTextDisplayExtensions,
    content,
    editable: false,
    immediatelyRender: false,
  })

  return (
    <EditorContent
      editor={editor}
      className='minimal-tiptap-editor max-w-none p-4 cursor-unset'
    />
  )
}

const NewsItem = ({
  item,
  inList = false,
  teamName,
  editPermission,
  canManage,
  newsPath,
  hideBack = false,
}: {
  item: wowaudit.NewsItem | wowaudit.GlobalNewsItem
  inList?: boolean
  teamName?: string
  editPermission?: NewsPermission
  canManage?: boolean
  newsPath?: string
  hideBack?: boolean
}) => {
  const { t } = useTranslation()
  const { customDomain } = useCustomDomain()
  const isGuildNews = 'newsableType' in item && item.newsableType === 'Guild'
  const tags = 'tags' in item ? item.tags : []
  const itemPath = newsPath ? `${newsPath}/${item.id}` : `${item.id}`
  let editPath = 'edit'
  if (newsPath) {
    editPath = `${newsPath}/${item.id}/edit`
  } else if (inList) {
    editPath = `${item.id}/edit`
  }

  const editLink = (
    <Link
      to={editPath}
      className={
        inList ? '' : buttonVariants({ variant: 'outline', size: 'sm' })
      }
    >
      <Pencil className='w-4 h-4' />
    </Link>
  )

  return (
    <Card className='p-0'>
      {!inList ? (
        <SeoHead
          title={siteTitle([item.title || 'News'], customDomain)}
          description={htmlExcerpt(item.content)}
          ogType='article'
          ogImage={item.mainImageUrl}
          robots={item.published ? 'index, follow' : 'noindex, nofollow'}
        />
      ) : null}
      <header
        className={cn(
          'p-4 border-b',
          inList ? 'border-accent' : 'border-b border-border',
        )}
      >
        <div className='flex justify-between gap-2'>
          {inList ? (
            <h3 className='mb-1'>
              <Link to={itemPath}>{item.title}</Link>
            </h3>
          ) : (
            <h2 className='mb-1'>{item.title}</h2>
          )}

          <div className='flex items-center gap-2'>
            {inList || hideBack ? null : (
              <Link
                to={newsPath ?? '..'}
                className={buttonVariants({ variant: 'outline', size: 'sm' })}
              >
                Back to news
              </Link>
            )}
            {canManage === undefined ? (
              editPermission ? (
                <ProtectedContent
                  section={editPermission.section}
                  requirement={editPermission.requirement}
                  renderUnauthorized={false}
                  requireAllTeams={editPermission.requireAllTeams}
                >
                  {editLink}
                </ProtectedContent>
              ) : null
            ) : canManage ? (
              editLink
            ) : null}
          </div>
        </div>

        {tags.length > 0 && (
          <div className='flex flex-wrap items-center gap-1.5 mt-2'>
            {tags.map((tag) => (
              <NewsTagBadge key={tag} tag={tag} />
            ))}
          </div>
        )}

        <div className='flex items-center gap-2 text-muted-foreground'>
          {item.firstPublishedAt && (
            <time dateTime={item.firstPublishedAt}>
              {dayjs(item.firstPublishedAt).format('MMMM D, YYYY')}
            </time>
          )}
          {item.createdBy && (
            <>
              <span>•</span>
              <span>{item.createdBy.obfuscatedBattletag ?? 'Hidden user'}</span>
            </>
          )}
          {isGuildNews || !teamName ? null : (
            <>
              <span>•</span>
              <span>
                {t('shared.team')} {teamName}
              </span>
            </>
          )}
        </div>
      </header>

      {item.mainImageUrl && (
        <div className='overflow-hidden'>
          <img
            src={item.mainImageUrl}
            alt={item.title}
            className={cn(
              'w-full h-auto max-h-[500px] object-cover',
              !inList && 'rounded-md border border-border',
            )}
          />
        </div>
      )}

      <NewsItemContent key={item.updatedAt} content={item.content} />
    </Card>
  )
}

export default NewsItem
